import * as Yup from "yup";
import { useFormik } from "formik";
import { useLingui } from "@lingui/react";
import { t, Trans } from "@lingui/macro";
import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useAlertStore } from "@/components/context/alert-store";
import { Box, Button, TextField, Typography } from "@mui/material";
import CustomCheckbox from "@/components/core/mui-custom-components/checkbox-custom";
import CountrySelector from "@/components/core/country-selector";
import PasswordField from "@/components/core/password-field";
import { useMutation } from "@tanstack/react-query";
import { useCheckoutStore } from "@/components/context/checkout-store";
import OfferzPlusTC from "@/components/core/offerz-plus-tc";
import { postRequest } from "@/utils/request";
import { useSession } from "next-auth/react";
import PrivacyModal from "@/components/core/privacy";

interface CheckboxData {
  isNewsletterChecked: boolean;
  isTermsChecked: boolean;
  isPrivacyChecked: boolean;
}

interface RegisterFormProps {
  activeTab: string;
  lang: string;
  fromCheckout?: boolean;
  onRegisterSuccess?: (email: string) => void;
}

interface FormData {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  passwordConfirm: string;
  phone: string;
}

interface MutationData {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  confirm: string;
  confirm_email: string;
  country_code: string;
  telephone: string;
  customer_group_id: number;
  agree: boolean;
  newsletter: boolean;
  offerz_plus_terms: boolean;
}

const RegisterForm: React.FC<RegisterFormProps> = ({
  activeTab,
  lang,
  fromCheckout = false,
  onRegisterSuccess,
}) => {
  const { i18n } = useLingui();
  const router = useRouter();
  const { data: session } = useSession();
  const [isGuest, setIsGuest] = useState<boolean>(false);
  useEffect(() => {
    const is_guest = session?.user.is_guest || "";
    setIsGuest(is_guest === "1");
  }, [session]);

  const mutation = useMutation({
    mutationFn: async (data: MutationData) => {
      await postRequest({ path: { name: "logout" }, method: "POST", lang });
      return postRequest({ path: { name: "register" }, body: data, method: "POST", lang });
    },
  });
  const updateMutation = useMutation({
    mutationFn: (data: object) =>
      postRequest({
        path: { name: "account" },
        body: { ...data },
        method: "PUT",
        lang,
      }),
  });
  const { setMessage } = useAlertStore();

  const [checkboxState, setCheckboxState] = useState<CheckboxData>({
    isNewsletterChecked: true,
    isTermsChecked: false,
    isPrivacyChecked: false,
  });

  const [errorState, setErrorState] = useState<{ [key: string]: string }>({});

  const [countryCode, setCountryCode] = useState<string>("41");

  const [openTermsModal, setOpenTermsModal] = useState<boolean>(false);
  const [openPrivacyModal, setOpenPrivacyModal] = useState<boolean>(false);
  const { setCheckoutOtp } = useCheckoutStore();

  const scriptRegex = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
  const onlyLettersRegex = /^[\p{L}' -]+$/u;

  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      email: session?.user.email || "",
      password: "",
      passwordConfirm: "",
      phone: "",
    },
    validationSchema: Yup.object({
      firstname: isGuest
        ? Yup.string()
        : Yup.string()
            .required(t(i18n)`First name must be between 1 and 32 characters!`)
            .test(
              "no-script-tags",
              t(i18n)`Invalid content detected`,
              (value) => !value || !scriptRegex.test(value),
            )
            .test(
              "only-letters",
              t(i18n)`Invalid characters detected`,
              (value) => !value || onlyLettersRegex.test(value),
            ),
      lastname: isGuest
        ? Yup.string()
        : Yup.string()
            .required(t(i18n)`Last name must be between 1 and 32 characters!`)
            .test(
              "no-script-tags",
              t(i18n)`Invalid content detected`,
              (value) => !value || !scriptRegex.test(value),
            )
            .test(
              "only-letters",
              t(i18n)`Invalid characters detected`,
              (value) => !value || onlyLettersRegex.test(value),
            ),
      email: Yup.string()
        .email(t(i18n)`Invalid email address`)
        .required(t(i18n)`Email is required`)
        .test(
          "no-script-tags",
          t(i18n)`Invalid content detected`,
          (value) => !value || !scriptRegex.test(value),
        ),
      password: Yup.string()
        .min(4, t(i18n)`Password must be at least 4 characters`)
        .max(32, t(i18n)`Password must be at most 32 characters`)
        .required(t(i18n)`Password is required`)
        .test(
          "no-script-tags",
          t(i18n)`Invalid content detected`,
          (value) => !value || !scriptRegex.test(value),
        ),
      passwordConfirm: Yup.string()
        .oneOf([Yup.ref("password")], t(i18n)`Password confirmation does not match password`)
        .required(t(i18n)`Confirm password is required`)
        .test(
          "no-script-tags",
          t(i18n)`Invalid content detected`,
          (value) => !value || !scriptRegex.test(value),
        ),
      phone: Yup.string()
        .required(t(i18n)`Phone is required!`)
        .test(
          "no-script-tags",
          t(i18n)`Invalid content detected`,
          (value) => !value || !scriptRegex.test(value),
        ),
    }),
    onSubmit: (values) => handleOnSubmit(values),
  });

  useEffect(() => {
    formik.setErrors({});
    setErrorState({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const savedFormData = localStorage.getItem("register-formData");
    if (savedFormData) {
      const parsedData = JSON.parse(savedFormData);
      const { checkboxState: savedCheckboxState, ...fieldValues } = parsedData;
      formik.setValues({
        firstname: fieldValues.firstname ?? "",
        lastname: fieldValues.lastname ?? "",
        email: fieldValues.email ?? "",
        password: fieldValues.password ?? "",
        passwordConfirm: fieldValues.passwordConfirm ?? "",
        phone: fieldValues.phone ?? "",
      });
      if (savedCheckboxState) {
        setCheckboxState(savedCheckboxState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setCheckboxState((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  const handleOnSubmit = (values: FormData) => {
    const formDataToSave = {
      ...formik.values,
      countryCode,
      checkboxState,
      customer_group_id:
        activeTab === "business" ? Number(process.env.NEXT_PUBLIC_BUSSINESS_CUSTOMER_GROUP) : 1, //register business account group ID - 10 for test, 11 for live
    };
    localStorage.removeItem("register-formData");
    localStorage.setItem("register-formData", JSON.stringify(formDataToSave));
    if (!checkboxState.isPrivacyChecked) {
      setMessage({
        text: t(i18n)`Warning: You must agree to the Privacy Policy!`,
        severity: "warning",
      });
      return;
    }

    if (!isGuest) {
      mutation.mutate(
        {
          firstname: values.firstname,
          lastname: values.lastname,
          email: values.email,
          password: values.password,
          confirm: values.passwordConfirm,
          confirm_email: "",
          country_code: countryCode,
          telephone: values.phone,
          customer_group_id:
            activeTab === "business" ? Number(process.env.NEXT_PUBLIC_BUSSINESS_CUSTOMER_GROUP) : 1, //register business account group ID - 10 for test, 11 for live
          agree: checkboxState.isPrivacyChecked,
          newsletter: checkboxState.isPrivacyChecked,
          offerz_plus_terms: checkboxState.isTermsChecked,
        },
        {
          onSuccess: (data) => {
            if (data.success === 0) {
              if (data.error_with_key) {
                setErrorState(data.error_with_key);
              } else {
                const errors: { telephone?: string } = {};
                if (data?.error?.telephone) {
                  errors.telephone = data.error.telephone;
                } else {
                  setMessage({
                    text: t(i18n)`An unexpected error occurred. Please try again later.`,
                    severity: "error",
                  });
                }
                setErrorState(errors);
              }
              return;
            }
            if (fromCheckout) {
              setCheckoutOtp(true);
              router.push("/checkout");
            } else {
              router.push("/register/otp");
            }
          },
          onError: (error: Error) => {
            console.error(error);
          },
        },
      );
    } else {
      updateMutation.mutate(
        {
          email: values.email,
          password: values.password,
          confirm: values.passwordConfirm,
          confirm_email: "",
          country_code: countryCode,
          telephone: values.phone,
          customer_group_id: 1,
          agree: checkboxState.isPrivacyChecked,
          newsletter: checkboxState.isPrivacyChecked,
          offerz_plus_terms: checkboxState.isTermsChecked,
          force_otp: 1,
        },
        {
          onSuccess: async (data: any) => {
            if (data.success === 0) {
              if (data.error_with_key.email) {
                setMessage({
                  text: data.error_with_key.email,
                  severity: "warning",
                });
              } else {
                setMessage({
                  text: t(i18n)`An unexpected error occurred. Please try again later.`,
                  severity: "error",
                });
              }
              return;
            }
            if (data.success) {
              onRegisterSuccess?.(values.email);
            }
          },
          onError: (error: any) => {
            console.error(error);
          },
        },
      );
    }
  };

  const toggleTermsModal = () => {
    setOpenTermsModal(!openTermsModal);
  };
  const togglePrivacyModal = () => {
    setOpenPrivacyModal(!openPrivacyModal);
  };

  const renderField = (name: keyof typeof formik.values, placeholder: string, type = "text") => (
    <Box sx={{ mb: "20px" }}>
      <TextField
        placeholder={placeholder}
        id={name}
        name={name}
        type={type}
        value={formik.values[name] || ""}
        onChange={formik.handleChange}
        error={!!errorState[name]}
        sx={{
          "& .MuiFormHelperText-root.Mui-error": {
            color: "#fd7b7c",
            fontSize: "12px",
          },
        }}
        helperText={errorState[name] ? errorState[name] : ""}
      />
      {formik.touched[name] && formik.errors[name] ? (
        <Typography sx={{ pt: "4px" }} variant="caption" color="#fd7b7c">
          {formik.errors[name]}
        </Typography>
      ) : null}
    </Box>
  );

  const termsLabelComponents = (
    <Trans>
      I have read and agree to the
      <Typography
        key="terms"
        variant="body2"
        component="span"
        onClick={(event) => {
          event.preventDefault();
          toggleTermsModal();
        }}
        sx={{ cursor: "pointer", fontWeight: 600, px: "4px" }}
      >
        Terms & Conditions
      </Typography>
      and I want to become an Offerz Plus member
    </Trans>
  );

  const privacyLabelComponents = (
    <Trans>
      I have read and agree to the
      <Typography
        key="privacy"
        variant="body2"
        component="span"
        onClick={(event) => {
          event.preventDefault();
          togglePrivacyModal();
        }}
        sx={{ cursor: "pointer", fontWeight: 600, px: "4px" }}
      >
        Privacy Policy
      </Typography>
    </Trans>
  );

  const textFieldRef = useRef<HTMLDivElement>(null);
  const [textFieldDimensions, setTextFieldDimensions] = useState({
    width: 0,
    top: 0,
    left: 0,
  });

  useEffect(() => {
    if (textFieldRef.current) {
      const rect = textFieldRef.current.getBoundingClientRect();
      setTextFieldDimensions({
        width: rect.width,
        top: rect.bottom,
        left: rect.left,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textFieldRef.current]);

  return (
    <>
      <OfferzPlusTC openTermsModal={openTermsModal} setOpenTermsModal={setOpenTermsModal} />
      <PrivacyModal
        open={openPrivacyModal}
        onClose={() => setOpenPrivacyModal(false)}
        lang={lang}
      />

      <form onSubmit={formik.handleSubmit}>
        {!isGuest && renderField("firstname", t(i18n)`First name`)}
        {!isGuest && renderField("lastname", t(i18n)`Last name`)}
        {renderField("email", t(i18n)`E-Mail`, "email")}

        <PasswordField
          name="password"
          label={t(i18n)`Password`}
          value={formik.values.password}
          formikError={formik.errors.password || ""}
          error={!!errorState.password}
          helperText={errorState.password || ""}
          handleChange={formik.handleChange}
          touched={formik.touched.password}
        />

        <PasswordField
          name="passwordConfirm"
          label={t(i18n)`Password confirm`}
          value={formik.values.passwordConfirm || ""}
          formikError={formik.errors.passwordConfirm}
          error={!!errorState.passwordConfirm}
          helperText={errorState.passwordConfirm || ""}
          handleChange={formik.handleChange}
          touched={formik.touched.passwordConfirm}
        />
        <Box sx={{ mb: "30px" }}>
          <TextField
            ref={textFieldRef}
            error={!!errorState.telephone}
            helperText={errorState.telephone || ""}
            placeholder={t(i18n)`Telephone`}
            id="phone"
            name="phone"
            type="tel"
            value={formik.values.phone}
            onInput={(e) => {
              const input = e.target as HTMLInputElement;
              input.value = input.value.replace(/\D/g, "");
            }}
            onChange={formik.handleChange}
            inputProps={{ maxLength: 9 }}
            InputProps={{
              startAdornment: (
                <CountrySelector
                  countryCode={countryCode}
                  setCountryCode={setCountryCode}
                  dimensions={textFieldDimensions}
                />
              ),
            }}
            sx={{
              "& .MuiFormHelperText-root.Mui-error": {
                color: "#fd7b7c",
                fontSize: "12px",
              },
              "& .MuiOutlinedInput-root": {
                padding: 0,
              },
            }}
          />
          {formik.touched.phone && formik.errors.phone ? (
            <Typography sx={{ pt: "16px" }} variant="caption" color="#fd7b7c">
              {formik.errors.phone}
            </Typography>
          ) : null}
        </Box>

        <div className="mb-10 flex flex-col gap-[15px]">
          <CustomCheckbox
            name="isNewsletterChecked"
            labelComponents={t(i18n)`I wish to subscribe to the Offerz newsletter`}
            checked={checkboxState.isNewsletterChecked}
            onChange={handleCheckboxChange}
          />
          {activeTab === "private" && (
            <CustomCheckbox
              name="isTermsChecked"
              labelComponents={termsLabelComponents}
              checked={checkboxState.isTermsChecked}
              onChange={handleCheckboxChange}
            />
          )}
          <CustomCheckbox
            name="isPrivacyChecked"
            labelComponents={privacyLabelComponents}
            checked={checkboxState.isPrivacyChecked}
            onChange={handleCheckboxChange}
          />
        </div>

        <Button
          data-testid="register-btn"
          type="submit"
          color="primary"
          variant="contained"
          fullWidth
          sx={{ mb: "50px", overflow: "hidden" }}
        >
          <Trans>Continue</Trans>
          <Typography
            component="span"
            className={`${mutation.isPending ? "loading" : ""}`}
          ></Typography>
        </Button>
      </form>
    </>
  );
};

export default RegisterForm;
